/*
 * Pure data builders for the Visão Geral "Contribution" card. No React/i18n,
 * so the shaping is unit-testable in the node environment.
 */
import type { PlayerStatistics, SegmentStatistics } from '@/types/statistics';
import { classifyShot, selectShots, type ShotEntry } from './shotTypes';

export interface PointLoss {
  /** Accepted error shots that went into the net. */
  net: number;
  /** Accepted error shots that bounced off the wall (out). */
  wall: number;
}

/**
 * How the player lost points: their accepted rally errors split into net vs
 * wall-bounce (the two error outcomes the pipeline tracks). Uses the same
 * accepted-rally-shot selection as every other count, so serves and
 * low-confidence detections are excluded. Returns null when there are no
 * tracked errors (so the card can hide gracefully).
 */
export function pointLossBreakdown(ps: PlayerStatistics): PointLoss | null {
  const errors = selectShots(ps, 'all').filter((s) => s.is_error);
  let net = 0;
  let wall = 0;
  for (const s of errors) {
    if (s.outcome.type === 'net') net++;
    else if (s.outcome.type === 'wall_bounce') wall++;
  }
  if (net + wall <= 0) return null;
  return { net, wall };
}

/** What each accepted rally shot adds to the Matchlytics Score. */
export const SCORE_POINTS = { error: -10, winner: 20, in: 1 };

export interface ScorePoint {
  /** Seconds since the block started. */
  t: number;
  value: number;
}

/** A running figure per player across the block: drawn as step lines. */
export interface PlayerSeries {
  /** Per player, the figure from 0, then after each shot that changed it, in the order played. */
  series: Record<string, ScorePoint[]>;
  /** The block's length, in seconds. */
  duration: number;
}

/** A player's accepted rally shots so far, by outcome. */
interface ShotCounts {
  in: number;
  error: number;
  winner: number;
}

/**
 * A running figure of every player's accepted rally shots across the block,
 * shot by shot in the order played (through the same classifier as every other
 * shot count): `value` turns their counts so far into the figure. `shotType`
 * narrows it to one shot chip (all by default). Null without shots (a basic
 * report).
 */
/**
 * A player's accepted rally shots of `shotType` in the order played, each at
 * its time in the block (seconds since it started).
 */
function shotsInOrder(
  segment: SegmentStatistics,
  id: string,
  shotType: string,
): { t: number; s: ShotEntry }[] {
  const ms = segment.statistics.match_summary;
  // Shots carry their video frame: placed on the block's clock through its frame rate.
  const fps = (ms.end_frame - ms.start_frame) / (ms.end_time - ms.start_time);
  const ps = segment.statistics.players_statistics[id];
  return (ps ? selectShots(ps, shotType) : [])
    .map((s) => ({ t: (s.shot_frame - ms.start_frame) / fps, s }))
    .sort((a, b) => a.t - b.t);
}

const blockDuration = (segment: SegmentStatistics) =>
  segment.statistics.match_summary.end_time -
  segment.statistics.match_summary.start_time;

function playerSeries(
  segment: SegmentStatistics,
  playerIds: string[],
  value: (counts: ShotCounts) => number,
  shotType = 'all',
): PlayerSeries | null {
  const series: Record<string, ScorePoint[]> = {};
  let any = false;
  for (const id of playerIds) {
    const shots = shotsInOrder(segment, id, shotType);
    any ||= shots.length > 0;
    const counts: ShotCounts = { in: 0, error: 0, winner: 0 };
    const line = [{ t: 0, value: value(counts) }];
    for (const { t, s } of shots) {
      const bucket = classifyShot(s);
      counts[bucket === 'error' || bucket === 'winner' ? bucket : 'in']++;
      const v = value(counts);
      if (v !== line[line.length - 1].value) line.push({ t, value: v });
    }
    series[id] = line;
  }
  return any ? { series, duration: blockDuration(segment) } : null;
}

/**
 * Each player's average ball speed (km/h) so far across the block, over their
 * accepted rally shots of `shotType` whose 3-D trajectory was fitted and
 * accepted (the shots the speed card averages). It starts at their first such
 * shot; null when no one has one.
 */
export function speedSeries(
  segment: SegmentStatistics,
  playerIds: string[],
  shotType = 'all',
): PlayerSeries | null {
  const series: Record<string, ScorePoint[]> = {};
  let any = false;
  for (const id of playerIds) {
    let sum = 0;
    let n = 0;
    series[id] = shotsInOrder(segment, id, shotType).flatMap(({ t, s }) => {
      const speed = s.trajectory?.speed_km_h;
      if (s.trajectory?.accepted === false || speed == null) return [];
      if (!Number.isFinite(speed)) return [];
      sum += speed;
      n += 1;
      return [{ t, value: sum / n }];
    });
    any ||= n > 0;
  }
  return any ? { series, duration: blockDuration(segment) } : null;
}

/**
 * The block's Matchlytics Score: every accepted rally shot of a player, in the
 * order they were played, adds +1 (in play), −10 (an error) or +20 (a winner).
 */
export const matchlyticsScore = (
  segment: SegmentStatistics,
  playerIds: string[],
) =>
  playerSeries(
    segment,
    playerIds,
    (c) =>
      c.in * SCORE_POINTS.in +
      c.error * SCORE_POINTS.error +
      c.winner * SCORE_POINTS.winner,
  );

/** `part` as a percentage of the accepted rally shots so far (0 before the first). */
const percentOfShots = (part: number, c: ShotCounts) =>
  (100 * part) / Math.max(c.in + c.error + c.winner, 1);

/** Each player's error rate so far (% of their accepted rally shots), across the block. */
export const errorRateSeries = (
  segment: SegmentStatistics,
  playerIds: string[],
  shotType = 'all',
) =>
  playerSeries(segment, playerIds, (c) => percentOfShots(c.error, c), shotType);

/** Each player's winner rate so far (% of their accepted rally shots), across the block. */
export const winnerRateSeries = (
  segment: SegmentStatistics,
  playerIds: string[],
  shotType = 'all',
) =>
  playerSeries(
    segment,
    playerIds,
    (c) => percentOfShots(c.winner, c),
    shotType,
  );

/**
 * Each player's winners per error so far, across the block. Before their first
 * error every winner counts in full (divided by one), so a clean sheet still
 * climbs instead of being undefined.
 */
export const winnersPerErrorSeries = (
  segment: SegmentStatistics,
  playerIds: string[],
  shotType = 'all',
) =>
  playerSeries(
    segment,
    playerIds,
    (c) => c.winner / Math.max(c.error, 1),
    shotType,
  );
